package com.playoffpulse.frontend.ui

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import com.playoffpulse.frontend.analytics.analyticsFailureSource
import com.playoffpulse.frontend.analytics.defaultPlayoffAnalytics
import com.playoffpulse.frontend.data.PlayoffDataProvider
import com.playoffpulse.frontend.data.defaultPlayoffDataProvider
import com.playoffpulse.frontend.routing.ALL_TEAMS_QUERY_VALUE
import com.playoffpulse.frontend.routing.ChartPageUrlState
import com.playoffpulse.frontend.routing.NhlAppUrlState
import com.playoffpulse.frontend.routing.NhlChartRoute
import com.playoffpulse.frontend.routing.NhlTeamRoute
import com.playoffpulse.frontend.routing.ParsedNhlTeamRoute
import com.playoffpulse.frontend.routing.SignalAttributionState
import com.playoffpulse.frontend.routing.StandingsPageUrlState
import com.playoffpulse.frontend.routing.TeamPageUrlState
import com.playoffpulse.frontend.state.AppUiState
import com.playoffpulse.frontend.state.PlayoffPulseController
import com.playoffpulse.shared.logic.MagicNumberCalculator
import com.playoffpulse.shared.logic.PlayoffObjectiveResolver
import com.playoffpulse.shared.logic.PlayoffTextFormatter
import com.playoffpulse.shared.model.ImportantGameEntry
import com.playoffpulse.shared.model.ImportantGamesResponse
import com.playoffpulse.shared.model.ImportantGamesWindow
import com.playoffpulse.shared.model.ImportanceTier
import com.playoffpulse.shared.model.AnalyticsPageKind
import com.playoffpulse.shared.model.BestCaseClinchResponse
import com.playoffpulse.shared.model.BlockedShotsMissesMode
import com.playoffpulse.shared.model.BestCaseClinchWitnessResult
import com.playoffpulse.shared.model.DraftBestCaseWitnessResult
import com.playoffpulse.shared.model.DraftLockedBelowEntry
import com.playoffpulse.shared.model.DraftRivalEntry
import com.playoffpulse.shared.model.DraftStatusProgressSummary
import com.playoffpulse.shared.model.DraftWatchResponse
import com.playoffpulse.shared.model.ImportantGamesObjective
import com.playoffpulse.shared.model.LeagueId
import com.playoffpulse.shared.model.MagicStatus
import com.playoffpulse.shared.model.NhlTeamCatalog
import com.playoffpulse.shared.model.RivalTiebreakState
import com.playoffpulse.shared.model.SnapshotMeta
import com.playoffpulse.shared.model.StandingsSnapshot
import com.playoffpulse.shared.model.TeamRef
import com.playoffpulse.shared.model.TeamStanding
import com.playoffpulse.shared.model.TopRivalEntry
import com.playoffpulse.shared.model.TopRivalsResponse
import com.playoffpulse.shared.model.VanquishProgressRow
import com.playoffpulse.shared.model.VanquishProgressSlotState
import com.playoffpulse.shared.model.VanquishProgressSummary
import com.playoffpulse.shared.model.VanquishedEntry
import com.playoffpulse.shared.model.VanquishedResponse
import com.playoffpulse.shared.model.displayRows
import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlin.js.Date
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Header
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Main
import org.jetbrains.compose.web.dom.Option
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Select
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.events.Event

private const val SHOW_SHARE_CARD = false
private const val SHOW_REFRESH_BUTTON = false
internal const val ALL_TEAMS_FILTER_VALUE = "__ALL_TEAMS__"
private const val NAV_COPY_LABEL = "Copy URL"
private const val NAV_COPIED_LABEL = "Copied"

@Composable
fun PlayoffPulseApp(provider: PlayoffDataProvider = defaultPlayoffDataProvider()) {
    val scope = rememberCoroutineScope()
    val controller = remember { PlayoffPulseController(provider) }
    val analytics = remember { defaultPlayoffAnalytics() }
    val initialUrlState = remember { NhlAppUrlState.parse(window.location.search) }
    var state by remember { mutableStateOf(AppUiState()) }
    var currentPage by remember { mutableStateOf(NhlAppPage.TEAM) }
    var currentChart by remember { mutableStateOf(NhlChartRoute.default) }
    var currentChartMissesMode by remember { mutableStateOf(initialUrlState.charts.missesMode) }
    var importantWindow by remember { mutableStateOf(initialUrlState.team.gamesWindow) }
    var importantImmediateOnly by remember { mutableStateOf(initialUrlState.team.immediateOnly) }
    var remainingTeamFilter by remember {
        mutableStateOf(initialUrlState.team.gamesTeam ?: ALL_TEAMS_FILTER_VALUE)
    }
    var standingsObjective by remember { mutableStateOf(initialUrlState.standings.objective) }
    var standingsMode by remember { mutableStateOf(initialUrlState.standings.viewMode) }
    var standingsSort by remember { mutableStateOf(initialUrlState.standings.sort) }
    var standingsSection by remember { mutableStateOf(initialUrlState.standings.section) }
    var pendingSignalAttribution by remember { mutableStateOf(initialUrlState.signalAttribution) }
    var teamPickerExpanded by remember { mutableStateOf(false) }
    var initialTeamTracked by remember { mutableStateOf(false) }
    var lastRouteViewKey by remember { mutableStateOf<String?>(null) }
    var lastErrorToken by remember { mutableStateOf<String?>(null) }
    var lastStaleToken by remember { mutableStateOf<String?>(null) }
    var navShareCopied by remember { mutableStateOf(false) }
    var chartShareCopied by remember { mutableStateOf(false) }
    val latestState by rememberUpdatedState(state)
    val isDraftPerspective = state.draftWatch?.active == true
    val hasRemainingImportantGames = if (isDraftPerspective) {
        state.importantGamesRemaining != null
    } else {
        state.bestCaseClinch?.scenarioAvailable == true
    }

    LaunchedEffect(Unit) {
        val bootstrapped = controller.bootstrap(
            preferredTeamId = NhlTeamRoute.candidateTeamId(window.location.pathname),
        )
        state = bootstrapped
        currentPage = pageForRoute(window.location.pathname, bootstrapped.teams)
        currentChart = chartForRoute(window.location.pathname, bootstrapped.teams)
        reconcileRouteAfterBootstrap(bootstrapped)
    }

    LaunchedEffect(state.snapshotMeta?.generatedAtIso) {
        if (state.teams.isEmpty()) return@LaunchedEffect
        state = controller.loadYesterdayMovement(state)
    }

    LaunchedEffect(currentPage, currentChartMissesMode, state.teams.size) {
        if (currentPage != NhlAppPage.CHARTS) return@LaunchedEffect
        if (state.teams.isEmpty()) return@LaunchedEffect
        if (
            (state.blockedShotsChart != null && state.blockedShotsChart?.selectedMissesMode == currentChartMissesMode) ||
            state.blockedShotsChartLoading
        ) {
            return@LaunchedEffect
        }
        val cachedBundle = state.blockedShotsChartBundles[currentChartMissesMode]
        if (cachedBundle != null) {
            state = state.copy(
                blockedShotsChart = cachedBundle.allChart,
                blockedShotsChartLoading = false,
                blockedShotsChartError = null,
                blockedShotsTeamDetails = emptyMap(),
                blockedShotsTeamDetailLoadingTeamIds = emptySet(),
                blockedShotsTeamDetailErrors = emptyMap(),
            )
            return@LaunchedEffect
        }
        state = state.copy(blockedShotsChartLoading = true, blockedShotsChartError = null)
        state = controller.loadBlockedShotsChart(state, currentChartMissesMode)
    }

    LaunchedEffect(state.selectedTeamId, hasRemainingImportantGames) {
        if (!hasRemainingImportantGames && importantWindow == ImportantGamesWindow.REMAINING) {
            importantWindow = ImportantGamesWindow.TODAY
        }
    }

    DisposableEffect(Unit) {
        val popStateHandler: (Event) -> Unit = popStateHandler@{
            val currentState = latestState
            if (currentState.teams.isEmpty()) return@popStateHandler
            val urlState = NhlAppUrlState.parse(window.location.search)
            applyParsedUrlState(
                urlState = urlState,
                setImportantWindow = { importantWindow = it },
                setImmediateOnly = { importantImmediateOnly = it },
                setRemainingTeamFilter = { remainingTeamFilter = it },
                setStandingsObjective = { standingsObjective = it },
                setStandingsMode = { standingsMode = it },
                setStandingsSort = { standingsSort = it },
                setStandingsSection = { standingsSection = it },
                setChartMissesMode = { currentChartMissesMode = it },
                setSignalAttribution = { pendingSignalAttribution = it },
            )

            scope.launch {
                applyRouteChange(
                    current = currentState,
                    controller = controller,
                    stateSetter = { state = it },
                    pageSetter = { currentPage = it },
                    chartSetter = { currentChart = it },
                )
            }
        }

        window.addEventListener("popstate", popStateHandler)
        onDispose {
            window.removeEventListener("popstate", popStateHandler)
        }
    }

    LaunchedEffect(state.summary?.team?.id) {
        val loadedTeamId = state.summary?.team?.id ?: return@LaunchedEffect
        if (!initialTeamTracked) {
            initialTeamTracked = true
            analytics.initialTeamLoaded(loadedTeamId)
        }
    }

    LaunchedEffect(navShareCopied) {
        if (!navShareCopied) return@LaunchedEffect
        delay(1_200)
        navShareCopied = false
    }

    LaunchedEffect(chartShareCopied) {
        if (!chartShareCopied) return@LaunchedEffect
        delay(1_200)
        chartShareCopied = false
    }

    LaunchedEffect(
        currentPage,
        state.selectedTeamId,
        importantWindow,
        importantImmediateOnly,
        remainingTeamFilter,
        standingsObjective,
        standingsMode,
        standingsSort,
        standingsSection,
        currentChart,
        currentChartMissesMode,
    ) {
        if (navShareCopied) {
            navShareCopied = false
        }
        if (chartShareCopied) {
            chartShareCopied = false
        }
    }

    LaunchedEffect(currentPage, currentChart, currentChartMissesMode, state.selectedTeamId, state.teams.size, pendingSignalAttribution.attributedTeamId) {
        if (state.teams.isEmpty()) return@LaunchedEffect
        val routeView = analyticsRouteView(
            page = currentPage,
            chart = currentChart,
            chartMissesMode = currentChartMissesMode,
            selectedTeamId = state.selectedTeamId,
            attributedTeamId = pendingSignalAttribution.attributedTeamId,
        ) ?: return@LaunchedEffect
        if (routeView.trackingKey == lastRouteViewKey) return@LaunchedEffect
        analytics.routeView(
            pageKind = routeView.pageKind,
            teamId = routeView.teamId,
            attributedTeamId = routeView.attributedTeamId,
        )
        lastRouteViewKey = routeView.canonicalKey
        if (pendingSignalAttribution.attributedTeamId != null) {
            pendingSignalAttribution = SignalAttributionState()
        }
    }

    LaunchedEffect(state.errorMessage) {
        val message = state.errorMessage ?: return@LaunchedEffect
        val token = "${state.summary?.team?.id ?: "NA"}|$message"
        if (lastErrorToken == token) return@LaunchedEffect
        lastErrorToken = token

        val teamId = state.summary?.team?.id
        analytics.loadFailed(
            source = analyticsFailureSource(message),
            teamId = teamId,
        )
    }

    LaunchedEffect(state.summary?.team?.id, state.snapshotMeta?.generatedAtIso, state.isStale) {
        val loadedTeamId = state.summary?.team?.id ?: return@LaunchedEffect
        if (!state.isStale) return@LaunchedEffect

        val staleToken = "$loadedTeamId|${state.snapshotMeta?.generatedAtIso ?: "none"}"
        if (lastStaleToken == staleToken) return@LaunchedEffect
        lastStaleToken = staleToken
        analytics.staleSnapshotSeen(loadedTeamId)
    }

    LaunchedEffect(
        currentPage,
        state.selectedTeamId,
        importantWindow,
        importantImmediateOnly,
        remainingTeamFilter,
        standingsObjective,
        standingsMode,
        standingsSort,
        standingsSection,
        currentChart,
        currentChartMissesMode,
        state.isLoading,
    ) {
        if (state.isLoading) return@LaunchedEffect
        val canonicalUrl = canonicalAppUrl(
            page = currentPage,
            chart = currentChart,
            selectedTeamId = state.selectedTeamId,
            teamState = TeamPageUrlState(
                gamesWindow = importantWindow,
                immediateOnly = importantImmediateOnly,
                gamesTeam = remainingTeamFilter
                    .takeUnless { it == ALL_TEAMS_FILTER_VALUE }
                    ?.uppercase(),
            ),
            standingsState = StandingsPageUrlState(
                objective = standingsObjective,
                viewMode = standingsMode,
                sort = standingsSort,
                section = standingsSection,
            ),
            chartState = ChartPageUrlState(missesMode = currentChartMissesMode),
        )
        val currentUrl = currentBrowserUrl()
        if (currentUrl != canonicalUrl) {
            window.history.replaceState(null, "", canonicalUrl)
        }
    }

    Main(attrs = { classes("app-shell") }) {
        Div(attrs = { classes("orb", "orb-left") })
        Div(attrs = { classes("orb", "orb-right") })

        Header(attrs = { classes("hero") }) {
            P(attrs = { classes("eyebrow") }) { Text("PLAYOFF RACE INTEL") }
            H1 { Text("Playoff Pulse") }
            P(attrs = { classes("hero-copy") }) {
                Text("Track every point swing with a nightly race snapshot.")
            }
        }

        PageNavigation(
            activePage = currentPage,
            onOpenTeam = {
                navShareCopied = false
                currentPage = NhlAppPage.TEAM
                val targetUrl = canonicalAppUrl(
                    page = NhlAppPage.TEAM,
                    chart = currentChart,
                    selectedTeamId = state.selectedTeamId,
                    teamState = TeamPageUrlState(
                        gamesWindow = importantWindow,
                        immediateOnly = importantImmediateOnly,
                        gamesTeam = remainingTeamFilter.takeUnless { it == ALL_TEAMS_FILTER_VALUE },
                    ),
                    standingsState = StandingsPageUrlState(
                        objective = standingsObjective,
                        viewMode = standingsMode,
                        sort = standingsSort,
                        section = standingsSection,
                    ),
                )
                if (currentBrowserUrl() != targetUrl) {
                    window.history.pushState(null, "", targetUrl)
                }
            },
            onOpenMovement = {
                navShareCopied = false
                val targetUrl = canonicalAppUrl(
                    page = NhlAppPage.MOVEMENT,
                    chart = currentChart,
                    selectedTeamId = state.selectedTeamId,
                    teamState = TeamPageUrlState(
                        gamesWindow = importantWindow,
                        immediateOnly = importantImmediateOnly,
                        gamesTeam = remainingTeamFilter.takeUnless { it == ALL_TEAMS_FILTER_VALUE },
                    ),
                    standingsState = StandingsPageUrlState(
                        objective = standingsObjective,
                        viewMode = standingsMode,
                        sort = standingsSort,
                        section = standingsSection,
                    ),
                )
                if (currentBrowserUrl() != targetUrl) {
                    window.history.pushState(null, "", targetUrl)
                }
                currentPage = NhlAppPage.MOVEMENT
            },
            onOpenStandings = {
                navShareCopied = false
                val targetUrl = canonicalAppUrl(
                    page = NhlAppPage.STANDINGS,
                    chart = currentChart,
                    selectedTeamId = state.selectedTeamId,
                    teamState = TeamPageUrlState(
                        gamesWindow = importantWindow,
                        immediateOnly = importantImmediateOnly,
                        gamesTeam = remainingTeamFilter.takeUnless { it == ALL_TEAMS_FILTER_VALUE },
                    ),
                    standingsState = StandingsPageUrlState(
                        objective = standingsObjective,
                        viewMode = standingsMode,
                        sort = standingsSort,
                        section = standingsSection,
                    ),
                )
                if (currentBrowserUrl() != targetUrl) {
                    window.history.pushState(null, "", targetUrl)
                }
                currentPage = NhlAppPage.STANDINGS
            },
            onOpenCharts = {
                navShareCopied = false
                val targetUrl = canonicalAppUrl(
                    page = NhlAppPage.CHARTS,
                    chart = currentChart,
                    selectedTeamId = state.selectedTeamId,
                    teamState = TeamPageUrlState(
                        gamesWindow = importantWindow,
                        immediateOnly = importantImmediateOnly,
                        gamesTeam = remainingTeamFilter.takeUnless { it == ALL_TEAMS_FILTER_VALUE },
                    ),
                    standingsState = StandingsPageUrlState(
                        objective = standingsObjective,
                        viewMode = standingsMode,
                        sort = standingsSort,
                        section = standingsSection,
                    ),
                    chartState = ChartPageUrlState(missesMode = currentChartMissesMode),
                )
                if (currentBrowserUrl() != targetUrl) {
                    window.history.pushState(null, "", targetUrl)
                }
                currentPage = NhlAppPage.CHARTS
            },
            onOpenAbout = {
                navShareCopied = false
                val targetUrl = canonicalAppUrl(
                    page = NhlAppPage.ABOUT,
                    chart = currentChart,
                    selectedTeamId = state.selectedTeamId,
                    teamState = TeamPageUrlState(
                        gamesWindow = importantWindow,
                        immediateOnly = importantImmediateOnly,
                        gamesTeam = remainingTeamFilter.takeUnless { it == ALL_TEAMS_FILTER_VALUE },
                    ),
                    standingsState = StandingsPageUrlState(
                        objective = standingsObjective,
                        viewMode = standingsMode,
                        sort = standingsSort,
                        section = standingsSection,
                    ),
                )
                if (currentBrowserUrl() != targetUrl) {
                    window.history.pushState(null, "", targetUrl)
                }
                currentPage = NhlAppPage.ABOUT
            },
            onOpenFaq = {
                navShareCopied = false
                val targetUrl = canonicalAppUrl(
                    page = NhlAppPage.FAQ,
                    chart = currentChart,
                    selectedTeamId = state.selectedTeamId,
                    teamState = TeamPageUrlState(
                        gamesWindow = importantWindow,
                        immediateOnly = importantImmediateOnly,
                        gamesTeam = remainingTeamFilter.takeUnless { it == ALL_TEAMS_FILTER_VALUE },
                    ),
                    standingsState = StandingsPageUrlState(
                        objective = standingsObjective,
                        viewMode = standingsMode,
                        sort = standingsSort,
                        section = standingsSection,
                    ),
                )
                if (currentBrowserUrl() != targetUrl) {
                    window.history.pushState(null, "", targetUrl)
                }
                currentPage = NhlAppPage.FAQ
            },
            shareButtonLabel = if (navShareCopied) NAV_COPIED_LABEL else NAV_COPY_LABEL,
            onCopyUrl = {
                val shareUrl = absoluteAppUrl(
                    canonicalAppUrl(
                        page = currentPage,
                        chart = currentChart,
                        selectedTeamId = state.selectedTeamId,
                        teamState = TeamPageUrlState(
                            gamesWindow = importantWindow,
                            immediateOnly = importantImmediateOnly,
                            gamesTeam = remainingTeamFilter.takeUnless { it == ALL_TEAMS_FILTER_VALUE },
                        ),
                        standingsState = StandingsPageUrlState(
                            objective = standingsObjective,
                            viewMode = standingsMode,
                            sort = standingsSort,
                            section = standingsSection,
                        ),
                        chartState = ChartPageUrlState(missesMode = currentChartMissesMode),
                    ),
                )
                navShareCopied = true
                scope.launch {
                    analytics.shareUrlCopied(
                        pageKind = when (currentPage) {
                            NhlAppPage.TEAM -> AnalyticsPageKind.TEAM
                            NhlAppPage.MOVEMENT -> AnalyticsPageKind.MOVEMENT
                            NhlAppPage.STANDINGS -> AnalyticsPageKind.STANDINGS
                            NhlAppPage.CHARTS -> AnalyticsPageKind.CHARTS
                            NhlAppPage.ABOUT -> AnalyticsPageKind.ABOUT
                            NhlAppPage.FAQ -> AnalyticsPageKind.FAQ
                        },
                        teamId = (state.summary?.team?.id ?: state.selectedTeamId).takeIf { currentPage == NhlAppPage.TEAM },
                    )
                }
                copyAbsoluteUrl(shareUrl)
            },
        )

        if (state.isLoading) {
            Div(attrs = { classes("important-card", "loading-panel") }) {
                P(attrs = { classes("meta-line") }) {
                    Text("Loading nightly standings...")
                }
            }
        }

        if (state.teams.isNotEmpty() && currentPage == NhlAppPage.TEAM) {
            Div(attrs = { classes("control-card") }) {
                Div(attrs = { classes("control-grid") }) {
                    Div {
                        val selectedTeam = selectedTeamRef(state)
                        val showExpandedPicker = teamPickerExpanded || selectedTeam == null

                        P(attrs = { classes("picker-label") }) {
                            Text(if (showExpandedPicker) "Choose Team" else "Current Team")
                        }

                        if (!showExpandedPicker) {
                            selectedTeam?.let { team ->
                                CompactSelectedTeam(
                                    team = team,
                                    disabled = state.isLoading || state.isRefreshing,
                                    onChangeTeam = { teamPickerExpanded = true },
                                )
                            }
                        } else {
                            selectedTeamDisplay(state)?.let { display ->
                                P(attrs = { classes("team-picker-selection") }) { Text(display) }
                            }
                            Div(attrs = { classes("team-picker-grid") }) {
                                state.teams.forEach { team ->
                                    TeamPickerTile(
                                        team = team,
                                        selected = team.id == state.selectedTeamId,
                                        disabled = state.isLoading || state.isRefreshing,
                                    ) {
                                        if (team.id == state.selectedTeamId && state.summary != null) {
                                            teamPickerExpanded = false
                                            return@TeamPickerTile
                                        }
                                        teamPickerExpanded = false
                                        remainingTeamFilter = ALL_TEAMS_FILTER_VALUE
                                        scope.launch {
                                            launch {
                                                analytics.teamSelected(team.id)
                                            }
                                            pushCanonicalUrl(
                                                canonicalAppUrl(
                                                    page = NhlAppPage.TEAM,
                                                    chart = currentChart,
                                                    selectedTeamId = team.id,
                                                    teamState = TeamPageUrlState(
                                                        gamesWindow = importantWindow,
                                                        immediateOnly = importantImmediateOnly,
                                                        gamesTeam = null,
                                                    ),
                                                    standingsState = StandingsPageUrlState(
                                                        objective = standingsObjective,
                                                        viewMode = standingsMode,
                                                        sort = standingsSort,
                                                        section = standingsSection,
                                                    ),
                                                ),
                                            )
                                            val loadingState = state.copy(
                                                selectedTeamId = team.id,
                                                summary = null,
                                                bestCaseClinch = null,
                                                topRivals = null,
                                                vanquished = null,
                                                draftWatch = null,
                                                importantGamesToday = null,
                                                importantGamesWeek = null,
                                                isRefreshing = true,
                                                errorMessage = null,
                                            )
                                            state = loadingState
                                            state = controller.selectTeam(loadingState, team.id)
                                        }
                                    }
                                }
                            }
                        }
                    }

                    if (SHOW_REFRESH_BUTTON) {
                        Div(attrs = { classes("button-row") }) {
                            Button(
                                attrs = {
                                    classes("pulse-button")
                                    if (state.isLoading || state.isRefreshing) {
                                        attr("disabled", "true")
                                    }
                                    onClick {
                                        scope.launch {
                                            state = state.copy(
                                                summary = null,
                                                bestCaseClinch = null,
                                                topRivals = null,
                                                vanquished = null,
                                                draftWatch = null,
                                                importantGamesToday = null,
                                                importantGamesWeek = null,
                                                isRefreshing = true,
                                                errorMessage = null,
                                            )
                                            state = controller.refresh(state)
                                        }
                                    }
                                },
                            ) {
                                Text(if (state.isRefreshing) "Refreshing..." else "Refresh Snapshot")
                            }
                        }
                    }
                }

                if (state.isStale) {
                    P(attrs = { classes("meta-warning") }) {
                        Text("Snapshot is stale. Data may be older than the nightly refresh window.")
                    }
                }

                state.errorMessage?.let { msg ->
                    P(attrs = { classes("meta-error") }) { Text(msg) }
                }
            }
        }

        if (state.teams.isNotEmpty() && currentPage != NhlAppPage.TEAM && currentPage != NhlAppPage.CHARTS && (state.isStale || state.errorMessage != null)) {
            Div(attrs = { classes("control-card") }) {
                if (state.isStale) {
                    P(attrs = { classes("meta-warning") }) {
                        Text("Snapshot is stale. Data may be older than the nightly refresh window.")
                    }
                }
                state.errorMessage?.let { msg ->
                    P(attrs = { classes("meta-error") }) { Text(msg) }
                }
            }
        }

        if (state.isRefreshing) {
            Div(attrs = { classes("important-card", "loading-panel") }) {
                H2 { Text("Loading Team") }
                P(attrs = { classes("meta-line") }) {
                    Text("Updating ${selectedTeamDisplay(state) ?: "team data"}...")
                }
            }
        }

        if (!state.isLoading && !state.isRefreshing && currentPage == NhlAppPage.TEAM) {
            state.summary?.let { summary ->
                if (isDraftPerspective) {
                    state.draftWatch?.let { draftWatch ->
                        DraftSummaryGrid(summary, draftWatch)
                        draftWatch.statusProgress?.let { progress ->
                            DraftStatusProgressStrip(progress)
                        }
                        DraftBestCaseScenarioSection(draftWatch, summary.team, state.teams)
                        DraftRivalsSection(draftWatch)
                    }
                } else {
                    val hasVanquished = state.vanquished?.vanquished?.isNotEmpty() == true
                    PlayoffSummaryGrid(summary, state.bestCaseClinch)
                    if (hasVanquished) {
                        VanquishedSection(
                            response = state.vanquished,
                            selectedTeamName = state.summary?.team?.displayName,
                            onCopyArticle = { path ->
                                val articleUrl = absoluteAppUrl(path)
                                scope.launch {
                                    analytics.shareUrlCopied(
                                        pageKind = AnalyticsPageKind.TEAM,
                                        teamId = summary.team.id,
                                    )
                                }
                                copyAbsoluteUrl(articleUrl)
                            },
                        )
                    }
                    if (hasRemainingImportantGames) {
                        EarliestClinchScenarioSection(
                            response = state.bestCaseClinch,
                            teams = state.teams,
                        )
                    } else if (!hasVanquished) {
                        TopRivalsSection(state.topRivals)
                        EarliestClinchScenarioSection(
                            response = state.bestCaseClinch,
                            teams = state.teams,
                        )
                    }
                    if (hasRemainingImportantGames && !hasVanquished) {
                        TopRivalsSection(state.topRivals)
                    }
                }

                Div(attrs = { classes("important-card") }) {
                    val response = when (importantWindow) {
                        ImportantGamesWindow.TODAY -> state.importantGamesToday
                        ImportantGamesWindow.WEEK -> state.importantGamesWeek
                        ImportantGamesWindow.REMAINING -> state.importantGamesRemaining
                    }
                    val remainingFilterOptions = remainingTeamFilterOptions(
                        response = state.importantGamesRemaining,
                        teams = state.teams,
                    )
                    LaunchedEffect(
                        remainingFilterOptions.joinToString("|") { it.value },
                    ) {
                        remainingTeamFilter = reconcileRemainingTeamFilter(
                            current = remainingTeamFilter,
                            options = remainingFilterOptions,
                        )
                    }
                    val isDraftObjective = response?.objective == ImportantGamesObjective.DRAFT
                    val introText = if (importantWindow == ImportantGamesWindow.REMAINING) {
                        if (isDraftObjective) {
                            "Season-end audit of ${summary.team.displayName}'s draft rivals and all remaining ${summary.team.displayName} games."
                        } else {
                            "Season-end audit of ${summary.team.displayName}'s earliest-clinch rivals and all remaining ${summary.team.displayName} games."
                        }
                    } else {
                        if (isDraftObjective) {
                            response?.draftTargetTag?.let { target ->
                                "Focused matchups that can move ${summary.team.displayName} toward $target."
                            } ?: "Focused matchups that can shape ${summary.team.displayName}'s draft path."
                        } else {
                            "Focused matchups that can reduce your remaining clinch path from ${ShareFormatter.pts(summary.magicPointsToClinch)}."
                        }
                    }
                    val detailText = if (importantWindow == ImportantGamesWindow.REMAINING) {
                        if (isDraftObjective) {
                            "Remaining shows the full draft watchlist, including games that can still shift ${summary.team.displayName}'s position even without an immediate move today."
                        } else {
                            "Remaining shows the full audit watchlist, including schedule-coupling games that may not have an immediate swing today."
                        }
                    } else {
                        if (isDraftObjective) {
                            response?.draftTargetTag?.let { target ->
                                "Immediate impact means direct progress toward $target now. Future pressure helps later, but provides no instant move. Immediate Only starts on by default."
                            } ?: "Immediate impact means direct draft-position movement now. Future pressure helps later, but provides no instant move. Immediate Only starts on by default."
                        } else {
                            "Immediate impact means direct clinch reduction now. Future pressure helps later, but provides no instant reduction. Immediate Only starts on by default."
                        }
                    }
                    H2 { Text("Important Games") }
                    P(attrs = { classes("meta-line") }) {
                        Text(introText)
                    }
                    P(attrs = { classes("meta-line") }) {
                        Text(detailText)
                    }

                    Div(attrs = { classes("important-controls") }) {
                        Div(attrs = { classes("important-tabs") }) {
                            Button(
                                attrs = {
                                    classes("tab-button")
                                    if (importantWindow == ImportantGamesWindow.TODAY) {
                                        classes("tab-button-active")
                                    }
                                    onClick {
                                        if (importantWindow == ImportantGamesWindow.TODAY) return@onClick
                                        importantWindow = ImportantGamesWindow.TODAY
                                        scope.launch {
                                            analytics.importantGamesTabViewed(
                                                teamId = state.summary?.team?.id,
                                                window = ImportantGamesWindow.TODAY,
                                            )
                                        }
                                    }
                                },
                            ) {
                                Text("Today")
                            }
                            Button(
                                attrs = {
                                    classes("tab-button")
                                    if (importantWindow == ImportantGamesWindow.WEEK) {
                                        classes("tab-button-active")
                                    }
                                    onClick {
                                        if (importantWindow == ImportantGamesWindow.WEEK) return@onClick
                                        importantWindow = ImportantGamesWindow.WEEK
                                        scope.launch {
                                            analytics.importantGamesTabViewed(
                                                teamId = state.summary?.team?.id,
                                                window = ImportantGamesWindow.WEEK,
                                            )
                                        }
                                    }
                                },
                            ) {
                                Text("This Week")
                            }
                            if (hasRemainingImportantGames) {
                                Button(
                                    attrs = {
                                        classes("tab-button")
                                        if (importantWindow == ImportantGamesWindow.REMAINING) {
                                            classes("tab-button-active")
                                        }
                                        onClick {
                                        if (importantWindow == ImportantGamesWindow.REMAINING) return@onClick
                                        importantImmediateOnly = false
                                        remainingTeamFilter = ALL_TEAMS_FILTER_VALUE
                                        importantWindow = ImportantGamesWindow.REMAINING
                                        scope.launch {
                                                analytics.importantGamesTabViewed(
                                                    teamId = state.summary?.team?.id,
                                                    window = ImportantGamesWindow.REMAINING,
                                                )
                                            }
                                        }
                                    },
                                ) {
                                    Text("Remaining")
                                }
                            }
                        }

                        Div(attrs = { classes("important-filters") }) {
                            Label(attrs = { classes("important-toggle") }) {
                                Input(
                                    type = InputType.Checkbox,
                                    attrs = {
                                        classes("important-toggle-input")
                                        checked(importantImmediateOnly)
                                        onChange { importantImmediateOnly = !importantImmediateOnly }
                                    },
                                )
                                Span(attrs = { classes("important-toggle-text") }) {
                                    Text("Immediate Only")
                                }
                            }

                            if (
                                importantWindow == ImportantGamesWindow.REMAINING &&
                                response != null
                            ) {
                                Label(attrs = { classes("important-select-label") }) {
                                    Span(attrs = { classes("important-toggle-text") }) {
                                        Text("Team")
                                    }
                                    Select(
                                        attrs = {
                                            classes("important-team-select")
                                            attr("value", remainingTeamFilter)
                                            onChange { event ->
                                                remainingTeamFilter = event.value ?: ALL_TEAMS_FILTER_VALUE
                                            }
                                        },
                                    ) {
                                        remainingFilterOptions.forEach { option ->
                                            Option(value = option.value) {
                                                Text(option.label)
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }

                    ImportantGamesContent(
                        response = response,
                        teams = state.teams,
                        immediateOnly = importantImmediateOnly,
                        remainingTeamFilter = if (importantWindow == ImportantGamesWindow.REMAINING) {
                            remainingTeamFilter
                        } else {
                            ALL_TEAMS_FILTER_VALUE
                        },
                    )
                }

                if (SHOW_SHARE_CARD) {
                    Div(attrs = { classes("share-card") }) {
                        H2 { Text("Share Card") }
                        P(attrs = { classes("share-headline") }) {
                            Text(
                                if (isDraftPerspective) {
                                    "${summary.team.displayName} | ${state.draftWatch?.currentDraftTag ?: "Draft Watch"} | Best ${state.draftWatch?.bestPossibleDraftSlotLabel ?: "—"}"
                                } else {
                                    "${summary.team.displayName} | ${ShareFormatter.statusLabel(summary)} | To clinch ${ShareFormatter.requirement(summary)}"
                                },
                            )
                        }
                        P {
                            Text(
                                if (isDraftPerspective) {
                                    state.draftWatch?.bestCaseNote
                                        ?: "Draft-first view tracks the best, worst, and next-better draft-position paths from the nightly snapshot."
                                } else {
                                    summary.note
                                },
                            )
                        }

                        Div(attrs = { classes("button-row") }) {
                            Button(attrs = {
                                classes("pulse-button")
                                onClick {
                                    scope.launch {
                                        val text = controller.shareText(state) ?: return@launch
                                        try {
                                            window.navigator.clipboard.writeText(text).await()
                                        } catch (_: Throwable) {
                                            window.prompt("Copy share text", text)
                                        }
                                    }
                                }
                            }) {
                                Text("Copy Share Text")
                            }
                        }
                    }
                }

                if (isDraftPerspective) {
                    LockedBelowSection(
                        response = state.draftWatch,
                        selectedTeamName = state.summary?.team?.displayName,
                        onCopyArticle = { path ->
                            val articleUrl = absoluteAppUrl(path)
                            scope.launch {
                                analytics.shareUrlCopied(
                                    pageKind = AnalyticsPageKind.TEAM,
                                    teamId = summary.team.id,
                                )
                            }
                            copyAbsoluteUrl(articleUrl)
                        },
                    )
                } else if (state.vanquished?.vanquished?.isNullOrEmpty() != false) {
                    VanquishedSection(
                        response = state.vanquished,
                        selectedTeamName = state.summary?.team?.displayName,
                        onCopyArticle = { path ->
                            val articleUrl = absoluteAppUrl(path)
                            scope.launch {
                                analytics.shareUrlCopied(
                                    pageKind = AnalyticsPageKind.TEAM,
                                    teamId = summary.team.id,
                                )
                            }
                            copyAbsoluteUrl(articleUrl)
                        },
                    )
                }
            }
        }

        if (!state.isLoading && !state.isRefreshing && currentPage == NhlAppPage.STANDINGS) {
            StandingsPage(
                teams = state.teams,
                standings = state.standings,
                standingsInsights = state.standingsInsights,
                snapshotMeta = state.snapshotMeta,
                selectedTeamId = state.selectedTeamId,
                objective = standingsObjective,
                onObjectiveChange = {
                    standingsObjective = it
                    standingsSort = defaultStandingsSort(it)
                },
                mode = standingsMode,
                onModeChange = {
                    standingsMode = it
                    standingsSection = null
                },
                sort = standingsSort,
                onSortChange = { standingsSort = it },
                sectionFilter = standingsSection,
            )
        }

        if (!state.isLoading && currentPage == NhlAppPage.CHARTS) {
            BlockedShotsChartPage(
                response = state.blockedShotsChart,
                selectedChart = currentChart,
                selectedMissesMode = currentChartMissesMode,
                onSelectChart = { chart ->
                    chartShareCopied = false
                    val targetUrl = canonicalAppUrl(
                        page = NhlAppPage.CHARTS,
                        chart = chart,
                        selectedTeamId = state.selectedTeamId,
                        teamState = TeamPageUrlState(
                            gamesWindow = importantWindow,
                            immediateOnly = importantImmediateOnly,
                            gamesTeam = remainingTeamFilter.takeUnless { it == ALL_TEAMS_FILTER_VALUE },
                        ),
                        standingsState = StandingsPageUrlState(
                            objective = standingsObjective,
                            viewMode = standingsMode,
                            sort = standingsSort,
                            section = standingsSection,
                        ),
                        chartState = ChartPageUrlState(missesMode = currentChartMissesMode),
                    )
                    if (currentBrowserUrl() != targetUrl) {
                        window.history.pushState(null, "", targetUrl)
                    }
                    currentChart = chart
                },
                onSelectMissesMode = { missesMode ->
                    chartShareCopied = false
                    val targetUrl = canonicalAppUrl(
                        page = NhlAppPage.CHARTS,
                        chart = currentChart,
                        selectedTeamId = state.selectedTeamId,
                        teamState = TeamPageUrlState(
                            gamesWindow = importantWindow,
                            immediateOnly = importantImmediateOnly,
                            gamesTeam = remainingTeamFilter.takeUnless { it == ALL_TEAMS_FILTER_VALUE },
                        ),
                        standingsState = StandingsPageUrlState(
                            objective = standingsObjective,
                            viewMode = standingsMode,
                            sort = standingsSort,
                            section = standingsSection,
                        ),
                        chartState = ChartPageUrlState(missesMode = missesMode),
                    )
                    if (currentBrowserUrl() != targetUrl) {
                        window.history.pushState(null, "", targetUrl)
                    }
                    currentChartMissesMode = missesMode
                    val cachedBundle = state.blockedShotsChartBundles[missesMode]
                    state = if (cachedBundle != null) {
                        state.copy(
                            blockedShotsChart = cachedBundle.allChart,
                            blockedShotsChartLoading = false,
                            blockedShotsChartError = null,
                            blockedShotsTeamDetails = emptyMap(),
                            blockedShotsTeamDetailLoadingTeamIds = emptySet(),
                            blockedShotsTeamDetailErrors = emptyMap(),
                        )
                    } else {
                        state.copy(
                            blockedShotsChart = null,
                            blockedShotsChartLoading = true,
                            blockedShotsChartError = null,
                            blockedShotsTeamDetails = emptyMap(),
                            blockedShotsTeamDetailLoadingTeamIds = emptySet(),
                            blockedShotsTeamDetailErrors = emptyMap(),
                        )
                    }
                },
                isLoading = state.blockedShotsChartLoading,
                errorMessage = state.blockedShotsChartError,
                teamDetails = state.blockedShotsTeamDetails,
                teamDetailLoadingTeamIds = state.blockedShotsTeamDetailLoadingTeamIds,
                teamDetailErrors = state.blockedShotsTeamDetailErrors,
                csvHref = blockedShotsCsvHref(currentChartMissesMode),
                copyButtonLabel = if (chartShareCopied) NAV_COPIED_LABEL else NAV_COPY_LABEL,
                onCopyUrl = {
                    val shareUrl = absoluteAppUrl(
                        canonicalAppUrl(
                            page = NhlAppPage.CHARTS,
                            chart = currentChart,
                            selectedTeamId = state.selectedTeamId,
                            teamState = TeamPageUrlState(
                                gamesWindow = importantWindow,
                                immediateOnly = importantImmediateOnly,
                                gamesTeam = remainingTeamFilter.takeUnless { it == ALL_TEAMS_FILTER_VALUE },
                            ),
                            standingsState = StandingsPageUrlState(
                                objective = standingsObjective,
                                viewMode = standingsMode,
                                sort = standingsSort,
                                section = standingsSection,
                            ),
                            chartState = ChartPageUrlState(missesMode = currentChartMissesMode),
                        ),
                    )
                    chartShareCopied = true
                    scope.launch {
                        analytics.shareUrlCopied(pageKind = AnalyticsPageKind.CHARTS)
                    }
                    copyAbsoluteUrl(shareUrl)
                },
                onLoadTeamDetail = { teamId ->
                    val normalizedTeamId = teamId.uppercase()
                    val cached = state.blockedShotsTeamDetails[normalizedTeamId]
                    if (
                        normalizedTeamId !in state.blockedShotsTeamDetailLoadingTeamIds &&
                        (cached == null || cached.selectedMissesMode != currentChartMissesMode)
                    ) {
                        scope.launch {
                            state = state.copy(
                                blockedShotsTeamDetailLoadingTeamIds = state.blockedShotsTeamDetailLoadingTeamIds + normalizedTeamId,
                                blockedShotsTeamDetailErrors = state.blockedShotsTeamDetailErrors - normalizedTeamId,
                            )
                            state = controller.loadBlockedShotsTeamDetail(
                                current = state,
                                teamId = normalizedTeamId,
                                missesMode = currentChartMissesMode,
                            )
                        }
                    }
                },
            )
        }

        if (!state.isLoading && !state.isRefreshing && currentPage == NhlAppPage.MOVEMENT) {
            YesterdayMovementSection(state.yesterdayMovement)
        }

        if (!state.isLoading && currentPage == NhlAppPage.ABOUT) {
            AboutPage(
                snapshotMeta = state.snapshotMeta,
                appVersion = appVersion(),
            )
        }

        if (!state.isLoading && currentPage == NhlAppPage.FAQ) {
            FaqPage()
        }

    }
}

@Composable
private fun CompactSelectedTeam(
    team: TeamRef,
    disabled: Boolean,
    onChangeTeam: () -> Unit,
) {
    Div(attrs = { classes("compact-team-picker") }) {
        Div(attrs = { classes("compact-team-selected") }) {
            Div(attrs = { classes("compact-team-logo-wrap") }) {
                Img(
                    src = TeamTilePalette.logoUrl(team.id),
                    attrs = {
                        classes("compact-team-logo")
                        attr("alt", "${team.displayName} logo")
                        attr("loading", "lazy")
                        attr("draggable", "false")
                    },
                )
            }
            Div(attrs = { classes("compact-team-copy") }) {
                P(attrs = { classes("compact-team-name") }) { Text(team.displayName) }
                P(attrs = { classes("compact-team-meta") }) { Text(team.abbreviation) }
            }
        }
        Button(
            attrs = {
                classes("compact-team-toggle")
                if (disabled) {
                    attr("disabled", "true")
                }
                onClick { onChangeTeam() }
            },
        ) {
            Text("Change Team")
        }
    }
}

@Composable
private fun TeamPickerTile(
    team: TeamRef,
    selected: Boolean,
    disabled: Boolean,
    onSelect: () -> Unit,
) {
    Button(
        attrs = {
            classes("team-tile")
            if (selected) {
                classes("team-tile-selected")
            }
            if (disabled) {
                attr("disabled", "true")
            }
            attr("title", team.displayName)
            attr("style", TeamTilePalette.inlineStyle(team.id))
            onClick { onSelect() }
        },
    ) {
        Div(attrs = { classes("team-tile-logo-wrap") }) {
            Img(
                src = TeamTilePalette.logoUrl(team.id),
                attrs = {
                    classes("team-tile-logo")
                    attr("alt", "${team.displayName} logo")
                    attr("loading", "lazy")
                    attr("draggable", "false")
                },
            )
        }
        Span(attrs = { classes("team-tile-abbrev") }) {
            Text(team.abbreviation)
        }
    }
}

@Composable
private fun PlayoffSummaryGrid(
    summary: com.playoffpulse.shared.model.MagicSummary,
    bestCaseClinch: BestCaseClinchResponse?,
) {
    Div(attrs = { classes("summary-grid") }) {
        Div(attrs = { classes("stat-card") }) {
            Span(attrs = { classes("stat-label") }) { Text("Status") }
            P(attrs = { classes("stat-value", statusClass(summary.status)) }) {
                Text(ShareFormatter.statusLabel(summary))
            }
            ShareFormatter.nextContendingGoal(summary)?.let { nextGoal ->
                P(attrs = { classes("stat-subtext") }) { Text(nextGoal) }
            }
        }

        Div(attrs = { classes("stat-card") }) {
            Span(attrs = { classes("stat-label") }) { Text("Current") }
            P(attrs = { classes("stat-value") }) { Text(ShareFormatter.current(summary.currentPoints, summary.maxPossiblePoints)) }
        }

        Div(attrs = { classes("stat-card") }) {
            Span(attrs = { classes("stat-label") }) { Text("Clinch Target (Any Seat)") }
            P(attrs = { classes("stat-value") }) { Text(ShareFormatter.pts(summary.clinchTargetPoints)) }
        }

        Div(attrs = { classes("stat-card") }) {
            Span(attrs = { classes("stat-label") }) { Text("To Clinch (Any Seat)") }
            P(attrs = { classes("stat-value") }) { Text(ShareFormatter.requirement(summary)) }
            if (summary.status == MagicStatus.CHASING) {
                P(attrs = { classes("stat-subtext") }) {
                    Text("${summary.team.displayName} needs wins plus outside help.")
                }
            }
        }

        if (summary.status != MagicStatus.CLINCHED && summary.status != MagicStatus.ELIMINATED) {
            bestCaseClinch?.takeIf { it.scenarioAvailable }?.let { bestCase ->
                val bestCaseWins = bestCase.bestCaseClinchWins
                val bestCasePoints = bestCase.bestCaseClinchPoints
                if (bestCaseWins != null && bestCasePoints != null) {
                    Div(attrs = { classes("stat-card") }) {
                        Span(attrs = { classes("stat-label") }) { Text("Best Case Clinch") }
                        P(attrs = { classes("stat-value") }) {
                            Text(
                                ShareFormatter.requirement(
                                    points = bestCasePoints,
                                    lowestWins = bestCaseWins,
                                    asterisk = false,
                                ),
                            )
                        }
                        bestCase.bestCaseClinchDateEt?.let { dateEt ->
                            P(attrs = { classes("stat-subtext") }) {
                                Text("Possible by ${formatBestCaseDate(dateEt)}")
                            }
                        }
                        bestCase.bestCaseClinchNote?.let { note ->
                            P(attrs = { classes("stat-subtext") }) { Text(note) }
                        }
                    }
                }
            }
        }

        if (summary.status == MagicStatus.CHASING) {
            Div(attrs = { classes("stat-card") }) {
                Span(attrs = { classes("stat-label") }) { Text("To Eliminate") }
                P(attrs = { classes("stat-value") }) { Text(ShareFormatter.requirement(summary.tragicPointsToElimination)) }
                P(attrs = { classes("stat-subtext") }) { Text("Points against ${summary.team.displayName}'s path.") }
            }
        }

        Div(attrs = { classes("stat-card") }) {
            Span(attrs = { classes("stat-label") }) { Text("Conference Cut Line") }
            P(attrs = { classes("stat-value") }) { Text(ShareFormatter.pts(summary.conferenceCutLinePoints)) }
        }

        Div(attrs = { classes("stat-card") }) {
            val gamesRemaining = gamesRemaining(summary)

            Span(attrs = { classes("stat-label") }) { Text("Games Remaining") }
            P(attrs = { classes("stat-value") }) { Text(gamesRemaining.toString()) }
            P(attrs = { classes("stat-subtext") }) { Text(gamesRemainingSubtext(summary, gamesRemaining)) }
        }
    }
}

@Composable
private fun DraftSummaryGrid(
    summary: com.playoffpulse.shared.model.MagicSummary,
    draftWatch: DraftWatchResponse,
) {
    Div(attrs = { classes("summary-grid") }) {
        Div(attrs = { classes("stat-card") }) {
            Span(attrs = { classes("stat-label") }) { Text("Status") }
            P(attrs = { classes("stat-value", "status-eliminated") }) {
                Text(draftWatch.currentDraftTag ?: "Draft Watch")
            }
            if (draftWatch.conditionalOnMissingPlayoffs) {
                P(attrs = { classes("stat-subtext") }) {
                    Text("These draft outcomes apply if ${summary.team.displayName} misses the playoffs.")
                }
            }
        }

        Div(attrs = { classes("stat-card") }) {
            Span(attrs = { classes("stat-label") }) { Text("Current") }
            P(attrs = { classes("stat-value") }) { Text(ShareFormatter.current(summary.currentPoints, summary.maxPossiblePoints)) }
        }

        Div(attrs = { classes("stat-card") }) {
            Span(attrs = { classes("stat-label") }) { Text("Current Tag") }
            P(attrs = { classes("stat-value") }) { Text(draftWatch.currentDraftTag ?: "—") }
            draftWatch.currentDraftSlotLabel?.let { label ->
                P(attrs = { classes("stat-subtext") }) { Text("Exact slot: $label") }
            }
        }

        Div(attrs = { classes("stat-card") }) {
            Span(attrs = { classes("stat-label") }) { Text("Best Possible") }
            P(attrs = { classes("stat-value") }) { Text(draftWatch.bestPossibleDraftSlotLabel ?: "—") }
        }

        Div(attrs = { classes("stat-card") }) {
            Span(attrs = { classes("stat-label") }) { Text("Worst Possible") }
            P(attrs = { classes("stat-value") }) { Text(draftWatch.worstPossibleDraftSlotLabel ?: "—") }
        }

        val betterTagLinePoints = draftWatch.betterTagLinePoints
        val nextBetterDraftTag = draftWatch.nextBetterDraftTag
        if (betterTagLinePoints != null && nextBetterDraftTag != null) {
            Div(attrs = { classes("stat-card") }) {
                Span(attrs = { classes("stat-label") }) { Text("Better Tag Line") }
                P(attrs = { classes("stat-value") }) { Text(ShareFormatter.pts(betterTagLinePoints)) }
                P(attrs = { classes("stat-subtext") }) { Text("Current line for $nextBetterDraftTag") }
            }
        }

        val pointsToBetterTag = draftWatch.pointsToBetterTag
        if (pointsToBetterTag != null && nextBetterDraftTag != null) {
            Div(attrs = { classes("stat-card") }) {
                Span(attrs = { classes("stat-label") }) { Text("To Better Tag") }
                P(attrs = { classes("stat-value") }) { Text(ShareFormatter.pts(pointsToBetterTag)) }
                P(attrs = { classes("stat-subtext") }) {
                    Text("Swing needed toward $nextBetterDraftTag.")
                }
            }
        }

        Div(attrs = { classes("stat-card") }) {
            Span(attrs = { classes("stat-label") }) { Text("To Slip") }
            P(attrs = { classes("stat-value") }) { Text(draftWatch.pointsToSlip?.let(ShareFormatter::pts) ?: "—") }
            P(attrs = { classes("stat-subtext") }) {
                Text(
                    draftWatch.nextWorseDraftTag?.let { "Cushion before slipping to $it." }
                        ?: "No lower draft tag remains.",
                )
            }
        }
    }
}

@Composable
private fun TopRivalsSection(response: TopRivalsResponse?) {
    Div(attrs = { classes("important-card") }) {
        H2 { Text("Top Rivals Driving To Clinch") }

        if (response == null) {
            P(attrs = { classes("meta-line") }) {
                Text("Loading top rivals...")
            }
            return@Div
        }

        if (response.topRivals.isEmpty()) {
            P(attrs = { classes("meta-line") }) {
                Text("No current rival is directly driving the clinch path.")
            }
            return@Div
        }

        Div(attrs = { classes("top-rival-list") }) {
            response.topRivals.forEach { rival ->
                TopRivalRow(rival)
            }
        }
    }
}

@Composable
private fun DraftRivalsSection(response: DraftWatchResponse?) {
    if (response == null) return

    Div(attrs = { classes("important-card") }) {
        H2 { Text("Draft Rivals") }

        if (response.draftRivals.isEmpty()) {
            P(attrs = { classes("meta-line") }) {
                Text("No nearby draft-order rivals are still swappable right now.")
            }
            return@Div
        }

        Div(attrs = { classes("top-rival-list") }) {
            response.draftRivals.forEach { rival ->
                DraftRivalRow(rival)
            }
        }
    }
}

@Composable
private fun TopRivalRow(rival: TopRivalEntry) {
    val tiebreakClass = when (rival.tiebreakState) {
        RivalTiebreakState.POINTS_ONLY -> "tier-secondary"
        RivalTiebreakState.SELECTED_EDGE -> "tier-primary"
        RivalTiebreakState.RIVAL_EDGE -> "tier-rival"
        RivalTiebreakState.UNRESOLVED -> "tier-neutral"
    }
    val tiebreakLabel = when (rival.tiebreakState) {
        RivalTiebreakState.POINTS_ONLY -> "Points"
        RivalTiebreakState.SELECTED_EDGE -> "Edge"
        RivalTiebreakState.RIVAL_EDGE -> "Rival Edge"
        RivalTiebreakState.UNRESOLVED -> "Unresolved"
    }

    Div(attrs = { classes("top-rival-row") }) {
        Div(attrs = { classes("important-game-header") }) {
            P(attrs = { classes("contender-name") }) {
                Text("${rival.team.displayName} (${rival.seatCode ?: rival.team.id})")
            }
            Div(attrs = { classes("important-game-badges") }) {
                if (rival.drivesClinchPoints) {
                    Span(attrs = { classes("tier-badge", "tier-secondary") }) {
                        Text("Points")
                    }
                }
                if (rival.drivesClinchWins) {
                    Span(attrs = { classes("tier-badge", "tier-primary") }) {
                        Text("Wins")
                    }
                }
                Span(attrs = { classes("tier-badge", tiebreakClass) }) {
                    Text(tiebreakLabel)
                }
            }
        }
        P(attrs = { classes("contender-meta") }) {
            Text("Now ${ShareFormatter.pts(rival.currentPoints)} | Max ${ShareFormatter.pts(rival.maxPossiblePoints)}")
        }
        P(attrs = { classes("contender-need") }) {
            Text("Moves out at: ${ShareFormatter.pts(rival.pressurePoints)}")
        }
        P(attrs = { classes("important-why") }) {
            Text(rival.explanation)
        }
    }
}

@Composable
private fun VanquishedSection(
    response: VanquishedResponse?,
    selectedTeamName: String?,
    onCopyArticle: (String) -> Unit,
) {
    if (response?.vanquished?.isEmpty() == true) return

    Div(attrs = { classes("important-card") }) {
        H2 { Text("Locked Behind") }

        if (response == null) {
            P(attrs = { classes("meta-line") }) {
                Text("Locked Behind analysis unavailable right now.")
            }
            return@Div
        }

        P(attrs = { classes("meta-line") }) {
            val teamLabel = selectedTeamName ?: "this team"
            Text("In-conference teams that can no longer finish ahead of $teamLabel's current point total.")
        }

        response.progress?.let { progress ->
            VanquishProgressStrip(progress)
        }

        Div(attrs = { classes("top-rival-list") }) {
            response.vanquished.forEach { rival ->
                VanquishedRow(rival, onCopyArticle)
            }
        }
    }
}

@Composable
private fun VanquishedRow(
    entry: VanquishedEntry,
    onCopyArticle: (String) -> Unit,
) {
    Div(attrs = { classes("top-rival-row") }) {
        P(attrs = { classes("contender-name") }) {
            Text(entry.team.displayName)
        }
        P(attrs = { classes("contender-meta") }) {
            Text("Now ${ShareFormatter.pts(entry.currentPoints)} | Max ${ShareFormatter.pts(entry.maxPossiblePoints)}")
        }
        P(attrs = { classes("important-why") }) {
            Text(entry.reason)
        }
        entry.tiebreakDetail?.let { detail ->
            P(attrs = { classes("important-why") }) {
                Text(detail)
            }
        }
        entry.publishedSignalPath?.let { path ->
            SignalArticleActions(path, onCopyArticle)
        }
    }
}

@Composable
private fun LockedBelowSection(
    response: DraftWatchResponse?,
    selectedTeamName: String?,
    onCopyArticle: (String) -> Unit,
) {
    if (response?.active != true || response.lockedBelow.isEmpty()) return

    Div(attrs = { classes("important-card") }) {
        H2 { Text("Locked Below") }

        P(attrs = { classes("meta-line") }) {
            val teamLabel = selectedTeamName ?: "this team"
            Text("Teams that can no longer finish with a better draft position than $teamLabel.")
        }

        Div(attrs = { classes("top-rival-list") }) {
            response.lockedBelow.forEach { rival ->
                DraftLockedBelowRow(rival, onCopyArticle)
            }
        }
    }
}

@Composable
private fun VanquishProgressStrip(
    summary: VanquishProgressSummary,
) {
    StatusProgressStrip(
        title = "Current Clinch Status",
        rows = summary.displayRows(),
    )
}

@Composable
private fun DraftStatusProgressStrip(
    summary: DraftStatusProgressSummary,
) {
    StatusProgressStrip(
        title = "Current Draft Status",
        rows = summary.rows,
    )
}

@Composable
private fun StatusProgressStrip(
    title: String,
    rows: List<VanquishProgressRow>,
) {
    if (rows.isEmpty()) return
    Div(attrs = {
        classes("vanquish-progress-strip")
        if (rows.size == 1) classes("vanquish-progress-strip-expanded")
    }) {
        H3(attrs = { classes("vanquish-progress-title") }) { Text(title) }
        rows.forEach { row ->
            VanquishProgressStripRow(row, expanded = rows.size == 1)
        }
    }
}

@Composable
private fun VanquishProgressStripRow(
    row: VanquishProgressRow,
    expanded: Boolean = false,
) {
    Div(attrs = {
        classes("vanquish-progress-strip-row")
        if (expanded) classes("vanquish-progress-strip-row-expanded")
    }) {
        Span(attrs = { classes("vanquish-progress-strip-label") }) {
            Text(row.seatCode)
        }
        Div(attrs = {
            classes("vanquish-progress-strip-slots")
            if (expanded) classes("vanquish-progress-strip-slots-expanded")
        }) {
            row.slots.forEach { slot ->
                val slotClasses = when (slot.state) {
                    VanquishProgressSlotState.FILLED -> arrayOf("vanquish-progress-strip-slot", "vanquish-progress-strip-slot-filled")
                    VanquishProgressSlotState.NEXT_UP -> arrayOf("vanquish-progress-strip-slot", "vanquish-progress-strip-slot-next")
                    VanquishProgressSlotState.EMPTY -> arrayOf("vanquish-progress-strip-slot", "vanquish-progress-strip-slot-empty")
                }
                Div(attrs = {
                    classes(*slotClasses)
                    slot.team?.displayName?.let { attr("title", it) }
                }) {
                    slot.team?.let { team ->
                        Img(
                            src = TeamTilePalette.logoUrl(team.id),
                            attrs = {
                                classes("vanquish-progress-strip-logo")
                                attr("alt", "${team.displayName} logo")
                                attr("loading", "lazy")
                            },
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun DraftBestCaseScenarioSection(
    response: DraftWatchResponse?,
    team: TeamRef,
    teams: List<TeamRef>,
) {
    if (response == null || !response.bestCaseScenarioAvailable || response.bestCaseWitnessGroups.isEmpty()) return

    Div(attrs = { classes("important-card") }) {
        H2 { Text("Earliest Draft Tier Scenario") }
        P(attrs = { classes("meta-line") }) {
            Text("All of the following must happen for ${team.displayName} to lock in ${response.bestCaseTargetDraftTag ?: "a major draft tier"} on the earliest possible path.")
        }

        response.bestCaseWitnessGroups.forEach { group ->
            Div(attrs = { classes("scenario-group") }) {
                P(attrs = { classes("meta-line") }) {
                    Text(formatBestCaseDate(group.dateEt))
                }
                Div(attrs = { classes("contender-list") }) {
                    group.results.forEach { result ->
                        P(attrs = { classes("contender-note") }) {
                            Text(renderDraftWitnessLine(result, teams))
                        }
                    }
                }
            }
        }

        response.bestCaseNote?.let { note ->
            P(attrs = { classes("meta-line") }) { Text(note) }
        }
    }
}

@Composable
private fun DraftRivalRow(entry: DraftRivalEntry) {
    Div(attrs = { classes("top-rival-row", "nested-rival-row") }) {
        P(attrs = { classes("contender-name") }) {
            Text("${entry.team.displayName} | Current ${draftTagLabel(entry.currentDraftSlot)}")
        }
        P(attrs = { classes("contender-meta") }) {
            Text("Now ${ShareFormatter.pts(entry.currentPoints)} | Max ${ShareFormatter.pts(entry.maxPossiblePoints)}")
        }
        P(attrs = { classes("important-why") }) {
            Text(entry.reason)
        }
    }
}

@Composable
private fun DraftLockedBelowRow(
    entry: DraftLockedBelowEntry,
    onCopyArticle: (String) -> Unit,
) {
    Div(attrs = { classes("top-rival-row", "nested-rival-row") }) {
        P(attrs = { classes("contender-name") }) {
            Text(entry.team.displayName)
        }
        P(attrs = { classes("contender-meta") }) {
            Text("Now ${ShareFormatter.pts(entry.currentPoints)} | Max ${ShareFormatter.pts(entry.maxPossiblePoints)}")
        }
        P(attrs = { classes("important-why") }) {
            Text(entry.reason)
        }
        entry.tiebreakDetail?.let { detail ->
            P(attrs = { classes("important-why") }) {
                Text(detail)
            }
        }
        entry.publishedSignalPath?.let { path ->
            SignalArticleActions(path, onCopyArticle)
        }
    }
}

@Composable
private fun SignalArticleActions(
    path: String,
    onCopyArticle: (String) -> Unit,
) {
    P(attrs = { classes("vanquished-share-row") }) {
        A(
            href = path,
            attrs = {
                classes("vanquished-share-link")
                attr("target", "_blank")
                attr("rel", "noopener noreferrer")
            },
        ) {
            Text("Open article")
        }
        Button(attrs = {
            classes("vanquished-copy-button")
            onClick { onCopyArticle(path) }
        }) {
            Text("Copy URL")
        }
    }
}

@Composable
private fun EarliestClinchScenarioSection(
    response: BestCaseClinchResponse?,
    teams: List<TeamRef>,
) {
    if (response == null || !response.scenarioAvailable || response.witnessGroups.isEmpty()) return
    val seatText = when {
        !response.bestCaseSeatLabel.isNullOrBlank() && !response.bestCaseSeatCode.isNullOrBlank() ->
            "${response.bestCaseSeatLabel} (${response.bestCaseSeatCode})"

        !response.bestCaseSeatLabel.isNullOrBlank() -> response.bestCaseSeatLabel
        !response.bestCaseSeatCode.isNullOrBlank() -> response.bestCaseSeatCode
        else -> "a playoff seat"
    }

    Div(attrs = { classes("important-card") }) {
        H2 { Text("Earliest Clinch Scenario") }
        P(attrs = { classes("meta-line") }) {
            Text("All of the following must happen for ${response.team.displayName} to clinch $seatText on the earliest possible path.")
        }

        response.witnessGroups.forEach { group ->
            Div(attrs = { classes("scenario-group") }) {
                P(attrs = { classes("meta-line") }) {
                    Text(formatBestCaseDate(group.dateEt))
                }
                Div(attrs = { classes("contender-list") }) {
                    group.results.forEach { result ->
                        P(attrs = { classes("contender-note") }) {
                            Text(renderWitnessLine(result, teams))
                        }
                    }
                }
            }
        }

        if (response.tiebreakNotes.isNotEmpty()) {
            P(attrs = { classes("meta-line") }) {
                Text("Also tied on points at the end of this sequence:")
            }
            Div(attrs = { classes("contender-list") }) {
                response.tiebreakNotes.forEach { note ->
                    P(attrs = { classes("contender-note") }) {
                        Text("${note.rivalTeam.displayName}: ${note.detail}")
                    }
                }
            }
        }
    }
}

@Composable
private fun ImportantGamesContent(
    response: ImportantGamesResponse?,
    teams: List<TeamRef>,
    immediateOnly: Boolean,
    remainingTeamFilter: String,
) {
    if (response == null) {
        P(attrs = { classes("meta-line") }) { Text("Loading important games...") }
        return
    }

    P(attrs = { classes("meta-line") }) {
        Text("Window: ${formatEtWindow(response.windowStartEtIso, response.windowEndEtIso)}")
    }

    val visibleGames = filterImportantGames(
        games = response.importantGames,
        immediateOnly = immediateOnly,
        selectedTeamId = if (response.window == ImportantGamesWindow.REMAINING) {
            remainingTeamFilter
        } else {
            ALL_TEAMS_FILTER_VALUE
        },
    )
    if (visibleGames.isEmpty()) {
        P(attrs = { classes("meta-line") }) {
            Text(
                if (immediateOnly && response.importantGames.any { it.importanceTier == ImportanceTier.FUTURE }) {
                    "No immediate-impact games in this window. Clear Immediate Only to see future-pressure games."
                } else {
                    "No qualifying games in this window."
                },
            )
        }
        return
    }

    Div(attrs = { classes("important-list") }) {
        visibleGames.forEach { game ->
            ImportantGameRow(
                game = game,
                teams = teams,
                objective = response.objective,
                draftTargetTag = response.draftTargetTag,
            )
        }
    }
}

internal fun filterImportantGames(
    games: List<ImportantGameEntry>,
    immediateOnly: Boolean,
    selectedTeamId: String = ALL_TEAMS_FILTER_VALUE,
): List<ImportantGameEntry> {
    val byTier = if (immediateOnly) {
        games.filter { it.importanceTier == ImportanceTier.IMMEDIATE }
    } else {
        games
    }
    return if (selectedTeamId == ALL_TEAMS_FILTER_VALUE) {
        byTier
    } else {
        byTier.filter { game ->
            game.awayTeamId.equals(selectedTeamId, ignoreCase = true) ||
                game.homeTeamId.equals(selectedTeamId, ignoreCase = true)
        }
    }
}

internal data class RemainingTeamFilterOption(
    val value: String,
    val label: String,
)

internal fun remainingTeamFilterOptions(
    response: ImportantGamesResponse?,
    teams: List<TeamRef>,
): List<RemainingTeamFilterOption> {
    if (response?.window != ImportantGamesWindow.REMAINING) {
        return emptyList()
    }

    val teamOptions = response.importantGames
        .flatMap { listOf(it.awayTeamId.uppercase(), it.homeTeamId.uppercase()) }
        .distinct()
        .map { teamId ->
            RemainingTeamFilterOption(
                value = teamId,
                label = displayTeamName(teamId, teams),
            )
        }
        .sortedBy { it.label }

    return listOf(RemainingTeamFilterOption(value = ALL_TEAMS_FILTER_VALUE, label = "All Teams")) + teamOptions
}

internal fun reconcileRemainingTeamFilter(
    current: String,
    options: List<RemainingTeamFilterOption>,
): String {
    return if (options.any { it.value == current }) current else ALL_TEAMS_FILTER_VALUE
}

@Composable
private fun ImportantGameRow(
    game: ImportantGameEntry,
    teams: List<TeamRef>,
    objective: ImportantGamesObjective,
    draftTargetTag: String? = null,
) {
    val tierClass = when (game.importanceTier) {
        ImportanceTier.IMMEDIATE -> "tier-primary"
        ImportanceTier.FUTURE -> "tier-secondary"
    }
    val tierLabel = when (game.importanceTier) {
        ImportanceTier.IMMEDIATE -> "Immediate"
        ImportanceTier.FUTURE -> "Future"
    }

    Div(attrs = { classes("important-game") }) {
        Div(attrs = { classes("important-game-header") }) {
            P(attrs = { classes("important-matchup") }) {
                Text("${displayTeam(game.awayTeamId, teams)} @ ${displayTeam(game.homeTeamId, teams)}")
            }
            Span(attrs = { classes("tier-badge", tierClass) }) {
                Text(tierLabel)
            }
        }

        P(attrs = { classes("important-time") }) {
            Text("${formatEtStart(game.startTimeIso)} | ${game.gameState}")
        }
        P(attrs = { classes("important-swing") }) {
            if (game.swingScore > 0) {
                Text(
                    when (objective) {
                        ImportantGamesObjective.PLAYOFF -> "Swing: ${ShareFormatter.pts(game.swingScore)} reduction"
                        ImportantGamesObjective.DRAFT -> "Swing: ${ShareFormatter.pts(game.swingScore)} toward ${draftTargetTag ?: "draft position"}"
                    },
                )
            } else {
                Text(
                    when (objective) {
                        ImportantGamesObjective.PLAYOFF -> "Swing: Chaser suppression"
                        ImportantGamesObjective.DRAFT -> draftTargetTag?.let { "Swing: Future $it cushion" } ?: "Swing: Draft-path cushion"
                    },
                )
            }
        }
        P(attrs = { classes("important-why") }) {
            Text(game.explanation)
        }
    }
}

private fun displayTeam(teamId: String, teams: List<TeamRef>): String {
    return teams.firstOrNull { it.id.equals(teamId, ignoreCase = true) }?.abbreviation ?: teamId
}

private fun displayTeamName(teamId: String, teams: List<TeamRef>): String {
    return teams.firstOrNull { it.id.equals(teamId, ignoreCase = true) }?.displayName ?: teamId.uppercase()
}

private fun renderWitnessLine(
    result: BestCaseClinchWitnessResult,
    teams: List<TeamRef>,
): String {
    val winnerName = displayTeamName(result.requiredWinnerTeamId, teams)
    val loserId = if (result.requiredWinnerTeamId.equals(result.awayTeamId, ignoreCase = true)) {
        result.homeTeamId
    } else {
        result.awayTeamId
    }
    val loserName = displayTeamName(loserId, teams)
    val suffix = if (result.regulationOnly) " in regulation." else "."
    return "$winnerName beat $loserName$suffix"
}

private fun renderDraftWitnessLine(
    result: DraftBestCaseWitnessResult,
    teams: List<TeamRef>,
): String {
    val winnerName = displayTeamName(result.requiredWinnerTeamId, teams)
    val loserId = if (result.requiredWinnerTeamId.equals(result.awayTeamId, ignoreCase = true)) {
        result.homeTeamId
    } else {
        result.awayTeamId
    }
    val loserName = displayTeamName(loserId, teams)
    return "$winnerName beat $loserName."
}

private fun selectedTeamRef(state: AppUiState): TeamRef? {
    val selectedTeamId = state.selectedTeamId ?: return null
    return state.teams.firstOrNull { it.id.equals(selectedTeamId, ignoreCase = true) }
}

private fun selectedTeamDisplay(state: AppUiState): String? {
    return selectedTeamRef(state)?.displayName
}

private fun appVersion(): String = runCatching {
    val value = js("window.PLAYOFFPULSE_APP_VERSION")
    (value as? String)?.takeIf { it.isNotBlank() } ?: "dev"
}.getOrDefault("dev")

private fun formatBestCaseDate(dateEt: String): String {
    return dateEt
}

private fun reconcileRouteAfterBootstrap(state: AppUiState) {
    when (val route = NhlTeamRoute.parse(window.location.pathname, state.teams)) {
        is ParsedNhlTeamRoute.Valid ->
            if (route.shouldCanonicalize) {
                window.history.replaceState(null, "", "${route.canonicalPath}${window.location.search.orEmpty()}")
            }

        is ParsedNhlTeamRoute.Invalid ->
            window.history.replaceState(null, "", NhlTeamRoute.rootPath())

        ParsedNhlTeamRoute.Base ->
            Unit

        ParsedNhlTeamRoute.About,
        ParsedNhlTeamRoute.Faq,
        ParsedNhlTeamRoute.Movement,
        ParsedNhlTeamRoute.Standings ->
            Unit

        is ParsedNhlTeamRoute.Charts ->
            if (route.shouldCanonicalize) {
                window.history.replaceState(null, "", route.canonicalPath)
            }

        ParsedNhlTeamRoute.Other,
            -> Unit
    }
}

private fun applyParsedUrlState(
    urlState: com.playoffpulse.frontend.routing.ParsedNhlAppUrlState,
    setImportantWindow: (ImportantGamesWindow) -> Unit,
    setImmediateOnly: (Boolean) -> Unit,
    setRemainingTeamFilter: (String) -> Unit,
    setStandingsObjective: (StandingsObjective) -> Unit,
    setStandingsMode: (StandingsViewMode) -> Unit,
    setStandingsSort: (StandingsSort) -> Unit,
    setStandingsSection: (String?) -> Unit,
    setChartMissesMode: (BlockedShotsMissesMode) -> Unit,
    setSignalAttribution: (SignalAttributionState) -> Unit,
) {
    setImportantWindow(urlState.team.gamesWindow)
    setImmediateOnly(urlState.team.immediateOnly)
    setRemainingTeamFilter(urlState.team.gamesTeam ?: ALL_TEAMS_FILTER_VALUE)
    setStandingsObjective(urlState.standings.objective)
    setStandingsMode(urlState.standings.viewMode)
    setStandingsSort(urlState.standings.sort)
    setStandingsSection(urlState.standings.section)
    setChartMissesMode(urlState.charts.missesMode)
    setSignalAttribution(urlState.signalAttribution)
}

private suspend fun applyRouteChange(
    current: AppUiState,
    controller: PlayoffPulseController,
    stateSetter: (AppUiState) -> Unit,
    pageSetter: (NhlAppPage) -> Unit,
    chartSetter: (NhlChartRoute) -> Unit,
) {
    when (val route = NhlTeamRoute.parse(window.location.pathname, current.teams)) {
        ParsedNhlTeamRoute.About -> {
            pageSetter(NhlAppPage.ABOUT)
        }

        ParsedNhlTeamRoute.Faq -> {
            pageSetter(NhlAppPage.FAQ)
        }

        ParsedNhlTeamRoute.Movement -> {
            pageSetter(NhlAppPage.MOVEMENT)
        }

        ParsedNhlTeamRoute.Standings -> {
            pageSetter(NhlAppPage.STANDINGS)
        }

        is ParsedNhlTeamRoute.Charts -> {
            pageSetter(NhlAppPage.CHARTS)
            chartSetter(route.chart)
            if (route.shouldCanonicalize) {
                window.history.replaceState(null, "", route.canonicalPath)
            }
        }

        is ParsedNhlTeamRoute.Valid -> {
            pageSetter(NhlAppPage.TEAM)
            if (route.shouldCanonicalize) {
                window.history.replaceState(null, "", "${route.canonicalPath}${window.location.search.orEmpty()}")
            }
            if (current.selectedTeamId == route.teamId && current.summary?.team?.id?.equals(route.teamId, ignoreCase = true) == true) {
                return
            }

            val loadingState = current.copy(
                selectedTeamId = route.teamId,
                summary = null,
                bestCaseClinch = null,
                topRivals = null,
                vanquished = null,
                draftWatch = null,
                importantGamesToday = null,
                importantGamesWeek = null,
                importantGamesRemaining = null,
                isRefreshing = true,
                errorMessage = null,
            )
            stateSetter(loadingState)
            stateSetter(controller.selectTeam(loadingState, route.teamId))
        }

        is ParsedNhlTeamRoute.Invalid -> {
            pageSetter(NhlAppPage.TEAM)
            window.history.replaceState(null, "", NhlTeamRoute.rootPath())
            stateSetter(
                current.copy(
                    selectedTeamId = null,
                    summary = null,
                    bestCaseClinch = null,
                    topRivals = null,
                    vanquished = null,
                    draftWatch = null,
                    importantGamesToday = null,
                    importantGamesWeek = null,
                    importantGamesRemaining = null,
                    isRefreshing = false,
                    errorMessage = null,
                ),
            )
        }

        ParsedNhlTeamRoute.Base -> {
            pageSetter(NhlAppPage.TEAM)
            stateSetter(
                current.copy(
                    selectedTeamId = null,
                    summary = null,
                    bestCaseClinch = null,
                    topRivals = null,
                    vanquished = null,
                    draftWatch = null,
                    importantGamesToday = null,
                    importantGamesWeek = null,
                    importantGamesRemaining = null,
                    isRefreshing = false,
                    errorMessage = null,
                ),
            )
        }

        ParsedNhlTeamRoute.Other -> Unit
    }
}

private data class RouteViewSpec(
    val canonicalKey: String,
    val trackingKey: String,
    val pageKind: AnalyticsPageKind,
    val teamId: String? = null,
    val attributedTeamId: String? = null,
)

private fun pageForRoute(
    pathname: String,
    teams: List<TeamRef>,
): NhlAppPage {
    return when (NhlTeamRoute.parse(pathname, teams)) {
        ParsedNhlTeamRoute.About -> NhlAppPage.ABOUT
        is ParsedNhlTeamRoute.Charts -> NhlAppPage.CHARTS
        ParsedNhlTeamRoute.Faq -> NhlAppPage.FAQ
        ParsedNhlTeamRoute.Movement -> NhlAppPage.MOVEMENT
        ParsedNhlTeamRoute.Standings -> NhlAppPage.STANDINGS
        else -> NhlAppPage.TEAM
    }
}

private fun analyticsRouteView(
    page: NhlAppPage,
    chart: NhlChartRoute,
    chartMissesMode: BlockedShotsMissesMode,
    selectedTeamId: String?,
    attributedTeamId: String?,
): RouteViewSpec? {
    return when (page) {
        NhlAppPage.TEAM -> {
            val teamId = selectedTeamId ?: return null
            RouteViewSpec(
                canonicalKey = "TEAM:$teamId",
                trackingKey = "TEAM:$teamId|ATTR:${attributedTeamId ?: "NA"}",
                pageKind = AnalyticsPageKind.TEAM,
                teamId = teamId,
                attributedTeamId = attributedTeamId,
            )
        }
        NhlAppPage.MOVEMENT -> RouteViewSpec("MOVEMENT", "MOVEMENT|ATTR:${attributedTeamId ?: "NA"}", AnalyticsPageKind.MOVEMENT, attributedTeamId = attributedTeamId)
        NhlAppPage.STANDINGS -> RouteViewSpec("STANDINGS", "STANDINGS|ATTR:${attributedTeamId ?: "NA"}", AnalyticsPageKind.STANDINGS, attributedTeamId = attributedTeamId)
        NhlAppPage.CHARTS -> RouteViewSpec(
            "CHARTS:${chart.slug}:${chartMissesMode.name}",
            "CHARTS:${chart.slug}:${chartMissesMode.name}|ATTR:${attributedTeamId ?: "NA"}",
            AnalyticsPageKind.CHARTS,
            attributedTeamId = attributedTeamId,
        )
        NhlAppPage.FAQ -> RouteViewSpec("FAQ", "FAQ|ATTR:${attributedTeamId ?: "NA"}", AnalyticsPageKind.FAQ, attributedTeamId = attributedTeamId)
        NhlAppPage.ABOUT -> RouteViewSpec("ABOUT", "ABOUT|ATTR:${attributedTeamId ?: "NA"}", AnalyticsPageKind.ABOUT, attributedTeamId = attributedTeamId)
    }
}

private fun canonicalAppUrl(
    page: NhlAppPage,
    chart: NhlChartRoute,
    selectedTeamId: String?,
    teamState: TeamPageUrlState,
    standingsState: StandingsPageUrlState,
    chartState: ChartPageUrlState = ChartPageUrlState(),
): String {
    return when (page) {
        NhlAppPage.TEAM -> {
            val path = selectedTeamId?.let { NhlTeamRoute.canonicalPath(it) } ?: NhlTeamRoute.rootPath()
            val query = if (selectedTeamId != null) NhlAppUrlState.teamQuery(teamState.copy(gamesTeam = normalizeTeamFilterForQuery(teamState.gamesTeam))) else ""
            "$path$query"
        }
        NhlAppPage.MOVEMENT -> NhlTeamRoute.movementPath()
        NhlAppPage.STANDINGS -> "${NhlTeamRoute.standingsPath()}${NhlAppUrlState.standingsQuery(standingsState)}"
        NhlAppPage.CHARTS -> "${NhlTeamRoute.chartsPath(chart)}${NhlAppUrlState.chartsQuery(chartState)}"
        NhlAppPage.FAQ -> NhlTeamRoute.faqPath()
        NhlAppPage.ABOUT -> NhlTeamRoute.aboutPath()
    }
}

private fun blockedShotsCsvHref(missesMode: BlockedShotsMissesMode): String {
    val suffix = if (missesMode == BlockedShotsMissesMode.INCLUDE) "" else "&misses=exclude"
    return "/api/v1/charts/blocked-shots.csv?league=NHL$suffix"
}

private fun chartForRoute(
    pathname: String,
    teams: List<TeamRef>,
): NhlChartRoute {
    return when (val route = NhlTeamRoute.parse(pathname, teams)) {
        is ParsedNhlTeamRoute.Charts -> route.chart
        else -> NhlChartRoute.default
    }
}

private fun normalizeTeamFilterForQuery(value: String?): String? {
    return value
        ?.uppercase()
        ?.takeUnless { it == ALL_TEAMS_FILTER_VALUE || it == ALL_TEAMS_QUERY_VALUE }
}

private fun defaultStandingsSort(objective: StandingsObjective): StandingsSort = StandingsSort(
    key = StandingsSortKey.PTS,
    direction = when (objective) {
        StandingsObjective.PLAYOFF -> StandingsSortDirection.DESC
        StandingsObjective.DRAFT -> StandingsSortDirection.ASC
    },
)

private fun currentBrowserUrl(): String {
    val pathname = window.location.pathname
    val search = window.location.search.orEmpty()
    return if (search.isBlank()) pathname else "$pathname$search"
}

private fun pushCanonicalUrl(url: String) {
    if (currentBrowserUrl() == url) return
    window.history.pushState(null, "", url)
}

private fun formatEtStart(iso: String): String {
    return runCatching {
        val date = Date(iso)
        if (date.getTime().isNaN()) return iso
        val options = js(
            "({ timeZone: 'America/New_York', weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })",
        )
        val formatted = date.toLocaleString("en-US", options) as String
        "$formatted ET"
    }.getOrElse { iso }
}

private fun formatEtWindow(startIso: String, endIso: String): String {
    val startDate = startIso.substringBefore('T')
    val endDate = endIso.substringBefore('T')
    return "$startDate to $endDate (ET)"
}

private fun ordinalLabel(value: Int): String {
    val suffix = when {
        value % 100 in 11..13 -> "th"
        value % 10 == 1 -> "st"
        value % 10 == 2 -> "nd"
        value % 10 == 3 -> "rd"
        else -> "th"
    }
    return "$value$suffix"
}

private fun draftTagLabel(value: Int): String = if (value <= 3) "1-3" else ordinalLabel(value)

private fun absoluteAppUrl(path: String): String {
    return if (path.startsWith("http://") || path.startsWith("https://")) {
        path
    } else {
        "${window.location.origin}$path"
    }
}

private fun copyAbsoluteUrl(url: String) {
    val clipboard = window.navigator.asDynamic().clipboard
    if (clipboard != null) {
        clipboard.writeText(url).catch { _: dynamic ->
            window.prompt("Copy URL", url)
        }
    } else {
        window.prompt("Copy URL", url)
    }
}

private fun statusClass(status: MagicStatus): String = when (status) {
    MagicStatus.CLINCHED -> "status-clinched"
    MagicStatus.IN_POSITION -> "status-in-position"
    MagicStatus.ELIMINATED -> "status-eliminated"
    MagicStatus.CHASING -> "status-chasing"
    MagicStatus.CONTROLS_DESTINY -> "status-contending"
}
